"use client";

import { useEffect, useRef } from "react";
import DailyIframe, { type DailyCall } from "@daily-co/daily-js";
import { PhoneOff } from "lucide-react";

import { Button } from "@/components/ui/button";

let dailyLock: Promise<void> = Promise.resolve();

function enqueueDaily(task: () => Promise<void>) {
  const run = dailyLock.then(task, task);
  dailyLock = run.then(
    () => undefined,
    () => undefined,
  );
  return run;
}

async function destroyDailyInstance(instance?: DailyCall | null) {
  const call = instance ?? DailyIframe.getCallInstance();
  if (!call) {
    return;
  }
  try {
    await call.destroy();
  } catch {
    // Már le volt állítva.
  }
}

export function VideoCallOverlay({
  roomUrl,
  token,
  title,
  onLeave,
}: {
  roomUrl: string;
  token: string;
  title: string;
  onLeave: () => void;
}) {
  const frameRef = useRef<HTMLDivElement>(null);
  const callRef = useRef<DailyCall | null>(null);
  const leftRef = useRef(onLeave);
  leftRef.current = onLeave;

  useEffect(() => {
    const host = frameRef.current;
    if (!host) {
      return;
    }

    let cancelled = false;
    let call: DailyCall | null = null;

    const handleLeft = () => {
      leftRef.current();
    };

    void enqueueDaily(async () => {
      await destroyDailyInstance(DailyIframe.getCallInstance());
      if (cancelled || !host.isConnected) {
        return;
      }

      call = DailyIframe.createFrame(host, {
        iframeStyle: {
          width: "100%",
          height: "100%",
          border: "0",
          borderRadius: "12px",
        },
        showLeaveButton: true,
        showFullscreenButton: true,
        showParticipantsBar: false,
      });
      callRef.current = call;

      if (cancelled) {
        await destroyDailyInstance(call);
        call = null;
        callRef.current = null;
        return;
      }

      call.on("left-meeting", handleLeft);
      await call.join({ url: roomUrl, token });
    });

    return () => {
      cancelled = true;
      const toDestroy = call;
      callRef.current = null;
      void enqueueDaily(async () => {
        const instance = toDestroy ?? DailyIframe.getCallInstance();
        if (!instance) {
          return;
        }
        instance.off("left-meeting", handleLeft);
        await destroyDailyInstance(instance);
      });
    };
  }, [roomUrl, token]);

  return (
    <div className="fixed inset-0 z-[80] flex flex-col bg-zinc-950/95 p-4">
      <div className="mb-3 flex items-center justify-between gap-3 text-white">
        <p className="truncate text-sm font-medium">{title}</p>
        <Button
          type="button"
          variant="destructive"
          size="sm"
          onClick={() => {
            const call = callRef.current;
            if (call) {
              void call.leave();
              return;
            }
            leftRef.current();
          }}
        >
          <PhoneOff className="size-4" />
          Hívás befejezése
        </Button>
      </div>
      <div ref={frameRef} className="min-h-0 flex-1 overflow-hidden rounded-xl bg-black" />
    </div>
  );
}
